import type {
  CleanupTypeVersion,
  LooseEndDecision,
  LooseEndSet,
  WizardData,
} from '../types';

import {
  flagTitle,
  groupArchiveSuggestion,
  namesSpanVisitCategories,
} from './versionSignals';

/**
 * A stale set whose most-used type still carries real lifetime usage isn't
 * pre-checked to archive — a busy type that's been quiet a few months reads
 * as seasonal, not dead. The admin decides; genuinely low-use stale sets
 * still archive by default. (GDN-2295 pilot: NPI pre-checked archiving of
 * 500–1174-visit types on recency alone.)
 */
const STALE_ARCHIVE_USAGE_CEILING = 100;

/** Multi-provider sets are the "your providers agree" story. */
export const isMultiProviderSet = (set: LooseEndSet): boolean =>
  new Set(set.entries.map((e) => e.version.provider ?? 'org')).size >= 2;

/**
 * Suggested surviving name: the most-used name WITHOUT a junk flag —
 * never suggest renaming a clean name to "RE-EVAL 6-30-25 Copy".
 */
export const suggestKeepName = (set: LooseEndSet): string => {
  const clean = set.entries.find((e) => !flagTitle(e.name));
  return clean?.name ?? set.entries[0]?.name ?? '';
};

const defaultsSignature = (v: CleanupTypeVersion): string =>
  JSON.stringify(v.defaults);

/**
 * Quick wins (GDN-2226): template clusters where every type is IDENTICAL —
 * same template, same defaults across the whole cluster; only the names
 * differ. These are zero-judgment consolidations ("your providers already
 * agree"), surfaced up front so the judgment lanes only handle what's
 * genuinely contested. Pool = everything the admin would otherwise see:
 * the automatic shared catalog + divergent-group versions + personal types.
 */
export const computeQuickWinSets = (data: WizardData): LooseEndSet[] => {
  const pool: CleanupTypeVersion[] = [];
  const seen = new Set<string>();
  for (const v of [
    ...data.sharedTypes,
    ...data.divergentGroups.flatMap((g) => g.versions),
    ...data.personalTypes,
  ]) {
    if (seen.has(v.id)) continue;
    seen.add(v.id);
    pool.push(v);
  }

  const byTemplate = new Map<string, CleanupTypeVersion[]>();
  for (const v of pool) {
    if (!v.templateId) continue;
    byTemplate.set(v.templateId, [...(byTemplate.get(v.templateId) ?? []), v]);
  }

  const sets: LooseEndSet[] = [];
  for (const [tid, versions] of byTemplate) {
    if (versions.length < 2) continue;
    // Homogeneous only — a single signature across the WHOLE cluster.
    // Partially-identical clusters stay in the judgment lanes.
    const signatures = new Set(versions.map(defaultsSignature));
    if (signatures.size !== 1) continue;
    sets.push({
      key: tid,
      templateTitle: versions[0].templateTitle,
      entries: [...versions]
        .sort((a, b) => b.usage - a.usage)
        .map((version) => ({ version, name: version.name })),
    });
  }
  // Pre-checked (multi-provider) sets first, then opt-in, size desc within.
  return sets.sort(
    (a, b) =>
      Number(isMultiProviderSet(b)) - Number(isMultiProviderSet(a)) ||
      b.entries.length - a.entries.length
  );
};

/**
 * Consumption-side normalization for a set decision: merges and digests
 * never see an empty keep-name — it falls back to the junk-free suggestion.
 * (The raw decision keeps whatever the admin typed, so the field can be
 * cleared mid-edit without the UI fighting back.)
 */
export const commitSetDecision = (
  set: LooseEndSet,
  decision: LooseEndDecision
): LooseEndDecision => ({
  ...decision,
  keepName: decision.keepName.trim() || suggestKeepName(set),
});

/** Whole set stale (no entry used recently) — merging would promote a dead type. */
export const isStaleSet = (set: LooseEndSet, now?: Date): boolean =>
  groupArchiveSuggestion(
    set.entries.map((e) => e.version),
    now
  ) !== null;

/**
 * Default decision for a quick-win / loose-end set:
 * - Whole set STALE → archive, not merge — consolidating types nobody has
 *   used in months just promotes a dead name into the shared catalog.
 * - Multi-provider and in use → merge ("your providers already agree").
 * - Single-provider → skip; distinct names ("Jaymie Intake" = "Progress
 *   Note" on one empty-defaults template) may be deliberate labels.
 * The suggested surviving name skips junk-flagged names.
 */
export const defaultQuickWinDecision = (
  set: LooseEndSet,
  now?: Date
): LooseEndDecision => {
  const stale = isStaleSet(set, now);
  if (set.near && !stale) {
    // Pre-check the high-confidence near-matches — one name is a token-subset
    // of another ("Intake" ⊂ "Psychiatric Intake") and they don't span visit
    // categories. Ambiguous near-matches stay opt-in. (GDN-2295 pilot: leaving
    // every near-match on Skip left the shared catalog full of near-dups.)
    const clearSubset =
      set.near.namesOverlap &&
      !namesSpanVisitCategories(set.entries.map((e) => e.name));
    return { merge: clearSubset, keepName: suggestKeepName(set) };
  }
  if (stale) {
    // Don't pre-check Archive when the most-used type still has real usage.
    const topUsage = set.entries[0]?.version.usage ?? 0;
    if (topUsage >= STALE_ARCHIVE_USAGE_CEILING) {
      return { merge: false, keepName: suggestKeepName(set) };
    }
    return { merge: false, archive: true, keepName: suggestKeepName(set) };
  }
  return { merge: isMultiProviderSet(set), keepName: suggestKeepName(set) };
};
